import { DATE_FORMAT, DATE_ISO8601_FORMAT } from '@/constants';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from 'antd';
import { useMemo } from 'react';

type DateInputProps = {
  /** Called with ISO8601 date string (YYYY-MM-DDTHH:mm:ssZ) or null when cleared */
  onChange: (date: string | null) => void;
  disabled?: boolean;
  /** Accepts ISO8601 datetime or YYYY-MM-DD date strings */
  value?: string | null;
  placeholder?: string;
  /** Forwarded to AntD `DatePicker` so call sites that need bespoke styling
   * (e.g. DetailsPanel's sunken-fill triggers) can pass arbitrary classes
   * without baking surface-specific styling into the primitive. */
  className?: string;
  /** Forwarded to AntD `DatePicker`. Default 'outlined' matches the AntD
   * default; pass 'borderless' alongside a sunken-fill `className` to get
   * the DetailsPanel form-row look. */
  variant?: 'outlined' | 'borderless' | 'filled';
  /** Forwarded to AntD `DatePicker`. Defaults to `'small'` — the typical
   * surface default for this primitive across the dashboard. AntD's own
   * default would be `'middle'` (taller); the default here preserves the
   * pre-PR rendering height for any caller that forgets to pass a size. */
  size?: 'small' | 'middle' | 'large';
};

export default function DateInput({
  value,
  onChange,
  disabled = false,
  placeholder,
  className,
  variant,
  size = 'small',
}: DateInputProps) {
  const dateValue = useMemo(() => {
    if (!value) return null;
    const iso = dayjs(value, DATE_ISO8601_FORMAT);
    if (iso.isValid()) return iso;
    const fallback = dayjs(value);
    return fallback.isValid() ? fallback : null;
  }, [value]);

  const handleChange = (date: Dayjs | null) => {
    if (date) {
      onChange(date.format(DATE_ISO8601_FORMAT));
    } else {
      onChange(null);
    }
  };

  return (
    <div>
      <DatePicker
        value={dateValue}
        onChange={handleChange}
        format={DATE_FORMAT}
        disabled={disabled}
        inputReadOnly={false}
        allowClear
        variant={variant}
        size={size}
        style={{ width: '100%' }}
        placeholder={placeholder ?? 'Select Date (YYYY-MM-DD)'}
        getPopupContainer={() => document.body}
        className={className}
      />
    </div>
  );
}
